import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type TextFieldInputProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

type TextFieldInputAdornmentProps = {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
  className?: string;
};

interface TextFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'onChange' | 'value'
> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium' | 'large';
  margin?: 'none' | 'dense' | 'normal';
  multiline?: boolean;
  context?: 'default' | 'modal';
  rows?: number;
  minRows?: number;
  maxRows?: number;
  InputProps?: TextFieldInputAdornmentProps;
  inputProps?: TextFieldInputProps;
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  value?: string | number | readonly string[] | null;
  sx?: React.CSSProperties;
}

const setRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  (ref as React.MutableRefObject<T | null>).current = value;
};

const mergeIds = (...values: Array<string | undefined>): string | undefined => {
  const ids = values.flatMap(
    (value) => value?.split(/\s+/).filter(Boolean) ?? []
  );

  return ids.length > 0 ? Array.from(new Set(ids)).join(' ') : undefined;
};

const TextField = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(
  (
    {
      className,
      label,
      helperText,
      error = false,
      fullWidth = false,
      variant,
      size = 'medium',
      margin = 'none',
      multiline = false,
      context = 'default',
      rows,
      minRows,
      maxRows,
      InputProps,
      inputProps,
      inputRef,
      onChange,
      sx,
      id,
      type = 'text',
      ...rest
    },
    ref
  ) => {
    void variant;

    const generatedId = React.useId();
    const fieldId = id ?? generatedId;
    const helperId = `${fieldId}-helper`;

    const describedBy = mergeIds(
      rest['aria-describedby'],
      inputProps?.['aria-describedby'],
      helperText ? helperId : undefined
    );

    const hasStartAdornment = Boolean(InputProps?.startAdornment);
    const hasEndAdornment = Boolean(InputProps?.endAdornment);

    const controlClassName = cn(
      !fullWidth && 'w-auto',
      size === 'small' && !multiline && 'h-9 text-sm',
      size === 'large' && !multiline && 'h-14 px-5 text-base',
      error &&
        'border-status-danger focus-visible:border-status-danger focus-visible:ring-[var(--color-ring-danger)]',
      hasStartAdornment && 'pl-10',
      hasEndAdornment && 'pr-12',
      InputProps?.className
    );

    const wrapperClassName = cn(
      (label || helperText) && 'flex flex-col gap-2',
      fullWidth && 'w-full',
      margin === 'dense' && 'my-1',
      margin === 'normal' && 'my-2',
      className
    );

    const sharedControlProps = {
      id: fieldId,
      'aria-invalid': error || undefined,
      ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      disabled: rest.disabled,
      required: rest.required,
      autoFocus: rest.autoFocus,
      placeholder: rest.placeholder,
      value: rest.value,
      defaultValue: rest.defaultValue,
      name: rest.name,
      autoComplete: rest.autoComplete,
      readOnly: InputProps?.readOnly ?? inputProps?.readOnly,
      onBlur: rest.onBlur,
      onFocus: rest.onFocus,
      onKeyDown: rest.onKeyDown,
      onKeyUp: rest.onKeyUp,
      onChange,
      tabIndex: rest.tabIndex,
      className: controlClassName,
    };

    return (
      <Field
        className={wrapperClassName}
        style={sx}
        data-invalid={error ? 'true' : undefined}
      >
        {label ? (
          <FieldLabel
            htmlFor={fieldId}
            className={cn(
              context === 'modal' && !error && 'text-[var(--color-neutral-25)]',
              error && 'text-status-danger'
            )}
          >
            {label}
          </FieldLabel>
        ) : null}
        <div className="relative">
          {hasStartAdornment && !multiline ? (
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-fg-secondary dark:text-[var(--color-neutral-25)] [&_svg]:h-4 [&_svg]:w-4">
              {InputProps?.startAdornment}
            </div>
          ) : null}
          {multiline ? (
            <Textarea
              ref={(node) => {
                setRef(ref, node);
                setRef(inputRef, node);
              }}
              context={context}
              rows={rows ?? minRows}
              style={
                minRows || maxRows
                  ? {
                      minHeight: minRows ? `${minRows * 1.5}rem` : undefined,
                      maxHeight: maxRows ? `${maxRows * 1.5}rem` : undefined,
                    }
                  : undefined
              }
              {...(rest as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              {...(inputProps as
                React.TextareaHTMLAttributes<HTMLTextAreaElement> | undefined)}
              {...(sharedControlProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          ) : (
            <Input
              ref={(node) => {
                setRef(ref, node);
                setRef(inputRef, node);
              }}
              type={type}
              context={context}
              {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
              {...(inputProps as
                React.InputHTMLAttributes<HTMLInputElement> | undefined)}
              {...(sharedControlProps as React.InputHTMLAttributes<HTMLInputElement>)}
              className={cn(controlClassName, 'w-full')}
            />
          )}
          {hasEndAdornment && !multiline ? (
            <div className="absolute inset-y-0 right-3 flex items-center text-fg-secondary dark:text-[var(--color-neutral-25)] [&_svg]:h-4 [&_svg]:w-4">
              {InputProps?.endAdornment}
            </div>
          ) : null}
        </div>
        {helperText ? (
          error ? (
            <FieldError id={helperId} className="text-status-danger">
              {helperText}
            </FieldError>
          ) : (
            <FieldDescription
              id={helperId}
              className={cn(
                context === 'modal' && 'text-[var(--color-neutral-25)]/80'
              )}
            >
              {helperText}
            </FieldDescription>
          )
        ) : null}
      </Field>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
